import { io, Socket } from "socket.io-client";
import { authService, sessionService } from "../../../util";

export const getBaseServerUrl = (): string | undefined => {
  // Built assets run on QA/Production hosts and derive the API origin from the page URL.
  if (import.meta.env.PROD) {
    const { protocol, host } = window.location;
    return `${protocol}//${host}`;
  }
  // Bracket access keeps Vitest from inlining an empty dev value into this module at transform time.
  // eslint-disable-next-line dot-notation -- runtime env lookup required for Vitest 4
  return import.meta.env["VITE_API_BASE_URL"] as string | undefined;
};

/**
 * The API path is specified separately as the `io` function only accepts a base URL
 */
export const getSocketPath = (): string => (import.meta.env.PROD ? "/api/socket.io" : "/socket.io");

/**
 * The socket client listens for server events and updates the user's page state in real time.
 * @param companyId - To join a room and receive company specific events
 */
export const initialiseSocket = async (isAdmin?: boolean, companyId?: string): Promise<Socket> => {
  const baseUrl = getBaseServerUrl();
  const path = getSocketPath();

  if (!baseUrl) {
    throw new Error("Base URL is not defined");
  }

  const authToken = await authService.getIdToken();
  const sessionId = sessionService.getCurrentSessionId();

  const socket = io(baseUrl, {
    path,
    transports: ["websocket"],
    auth: {
      token: authToken,
      sessionId,
    },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
  });

  if (isAdmin && companyId) {
    const joinCompanyRoom = (): void => {
      socket.emit("joinRoom", companyId);
    };

    socket.on("connect", joinCompanyRoom);

    if (socket.connected) {
      joinCompanyRoom();
    }
  }

  return socket;
};
